import { Fragment, useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Check, Undo2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { hasAcademyAccess } from "@/lib/academyAccess";
import { money, fmtDate, PAYMENT_METHOD_LABELS, selectCls, btnBrand } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const PAYOUT_METHODS = ["transfer", "zelle", "cash", "other"];
const localMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const pct = (n) => `${Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`;

function Stat({ label, value, testId, money: isMoney = true, tone }) {
  return (
    <div className="bg-white border border-border rounded-xl p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-medium mt-1 ${tone || ""}`} data-testid={testId}>{isMoney ? money(value) : value}</p>
    </div>
  );
}

function PayoutBadge({ payout, current }) {
  if (!payout) return null;
  const diff = Math.round((current - payout.amount) * 100) / 100;
  return (
    <span className="text-xs text-[#5B7A54] inline-flex items-center gap-1">
      <Check className="w-3 h-3" /> Pagada {money(payout.amount)} el {fmtDate(payout.date)}
      {Math.abs(diff) >= 0.01 && <span className="text-[#A34B49]"> · recalculada {money(current)} ({diff > 0 ? "+" : ""}{money(diff)})</span>}
    </span>
  );
}

function PayoutDialog({ target, month, onClose, onDone }) {
  const [method, setMethod] = useState("transfer");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setMethod("transfer"); setNote(""); setError(""); }, [target]);
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post("/academy/payouts", { kind: target.kind, person_id: target.id, month, method, note: note.trim() || null });
      onDone();
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={Boolean(target)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={ACADEMY.payoutModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Registrar pago</DialogTitle>
          <DialogDescription>{target ? `${target.name}: ${money(target.amount)} de ${month}. Queda anotado como gasto del mes.` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Cómo se paga</Label>
            <select className={selectCls} value={method} onChange={(e) => setMethod(e.target.value)} data-testid={ACADEMY.payoutMethodSelect}>
              {PAYOUT_METHODS.map((m) => <option key={m} value={m}>{m === "cash" ? "Efectivo (sale de la caja abierta)" : PAYMENT_METHOD_LABELS[m] || m}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nota (opcional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: número de transferencia" data-testid={ACADEMY.payoutNoteInput} />
          </div>
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} disabled={saving} data-testid={ACADEMY.payoutSaveButton}>{saving ? "Guardando..." : "Registrar pago"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExecutiveDetail({ executiveId, month }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/academy/commissions/${executiveId}/payments`, { params: { month } }).then(({ data }) => setD(data)).catch(() => setD({ payments: [], refunds: [] }));
  }, [executiveId, month]);
  if (!d) return <p className="text-sm text-muted-foreground px-3 py-2">Cargando...</p>;
  return (
    <div className="space-y-3" data-testid={`${ACADEMY.commissionDetailPrefix}${executiveId}`}>
      {d.payments.length === 0 && d.refunds.length === 0 && <p className="text-sm text-muted-foreground">Sin cobros en el mes.</p>}
      {d.payments.length > 0 && (
        <div className="divide-y divide-border border border-border rounded-lg bg-white">
          {d.payments.map((p) => (
            <div key={p.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm ${p.counts ? "" : "text-muted-foreground"}`}>
              <span className="w-24">{fmtDate(p.date)}</span>
              <span className="flex-1 min-w-[140px]">{p.student_name} · {p.course_name}</span>
              <span>{PAYMENT_METHOD_LABELS[p.method] || p.method}{p.is_reservation ? " · reserva" : ""}</span>
              <span className={`font-medium w-24 text-right ${p.counts ? "" : "line-through"}`}>{money(p.amount)}</span>
              {!p.counts && <span className="w-full text-xs">{p.why_not}</span>}
            </div>
          ))}
        </div>
      )}
      {d.refunds.length > 0 && (
        <div className="divide-y divide-border border border-border rounded-lg bg-white">
          {d.refunds.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm text-[#A34B49]">
              <span className="w-24">{fmtDate(r.date)}</span><span className="flex-1">Reembolso: {r.student_name} · {r.reason}</span><span className="font-medium">-{money(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Panel del ejecutivo: lo suyo del mes y cuanto le falta para el tramo siguiente.
function MyPanel({ row, month, tiers }) {
  const top = tiers.length > 1 ? tiers[row.tier]?.up_to : null;
  const prev = row.tier > 0 ? tiers[row.tier - 1].up_to : 0;
  const progress = top ? Math.min(100, Math.max(0, ((row.base - prev) / (top - prev)) * 100)) : 100;
  return (
    <div className="space-y-5" data-testid={ACADEMY.commissionMyPanel}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Interesados del mes" value={row.leads} money={false} testId={ACADEMY.commissionMyLeads} />
        <Stat label="Contactados" value={row.contacted} money={false} />
        <Stat label="Inscripciones (cerradas / enviadas)" value={`${row.closed} / ${row.enrollments}`} money={false} testId={ACADEMY.commissionMyClosed} />
        <Stat label="Vendido (cerradas)" value={row.sold} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Stat label="Cobrado en el mes" value={row.collected} testId={ACADEMY.commissionMyCollected} />
        <Stat label="Base de la comisión (cobrado - reembolsos)" value={row.base} />
        <Stat label={`Comisión (${pct(row.percent)})`} value={row.commission} tone="text-[#5B7A54]" testId={ACADEMY.commissionMyCommission} />
      </div>
      <div className="bg-white border border-border rounded-xl p-5 space-y-2">
        {row.next_percent != null ? (
          <>
            <p className="text-sm" data-testid={ACADEMY.commissionMyNext}>Te faltan <strong>{money(row.missing_for_next)}</strong> cobrados para pasar al {pct(row.next_percent)}.</p>
            <div className="h-2 rounded-full bg-[#E9F3F3] overflow-hidden"><div className="h-full bg-brand" style={{ width: `${progress}%` }} /></div>
          </>
        ) : (
          <p className="text-sm" data-testid={ACADEMY.commissionMyNext}>Estás en el tramo más alto ({pct(row.tier_percent)}).</p>
        )}
        <PayoutBadge payout={row.payout} current={row.commission} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-2">Lo que se cobró en el mes</p>
        <ExecutiveDetail executiveId={row.executive_id} month={month} />
      </div>
    </div>
  );
}

// Fix X (2026-10-13): comisiones de los ejecutivos (sobre lo cobrado en el
// mes) y lo que gana cada instructor por las fechas del mes.
export default function AcademyCommissionsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isFinance = hasAcademyAccess(user, ["finance"]);
  const isExecutive = hasAcademyAccess(user, ["executive"]);
  const isInstructor = hasAcademyAccess(user, ["instructor"]);
  const [month, setMonth] = useState(localMonth());
  const [tab, setTab] = useState(isFinance || isExecutive ? "executives" : "instructors");
  const [com, setCom] = useState(null);
  const [ins, setIns] = useState(null);
  const [open, setOpen] = useState({});
  const [payTarget, setPayTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      if (isFinance || isExecutive) setCom((await api.get("/academy/commissions", { params: { month } })).data);
      if (isFinance || isInstructor) setIns((await api.get("/academy/instructor-earnings", { params: { month } })).data);
    } catch (err) {
      toast({ title: "Error al cargar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  }, [month, isFinance, isExecutive, isInstructor, toast]);

  useEffect(() => { load(); }, [load]);

  const undo = async (payout) => {
    try {
      await api.delete(`/academy/payouts/${payout.id}`);
      toast({ title: "Pago deshecho" });
      load();
    } catch (err) {
      toast({ title: "No se pudo deshacer", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const myRow = !isFinance && com ? com.executives[0] : null;

  return (
    <div className="space-y-6" data-testid={ACADEMY.commissionsPage}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Comisiones</h1>
          <p className="text-base text-muted-foreground mt-1">
            {isFinance ? "Ejecutivos: sobre lo cobrado en el mes. Instructores: por las fechas del mes." : isExecutive ? "Tu mes: lo que cobraste y tu comisión." : "Lo que te corresponde por las fechas del mes."}
          </p>
        </div>
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Mes</Label>
          <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44" data-testid={ACADEMY.commissionsMonthInput} />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        {(isFinance || (isExecutive && isInstructor)) && (
          <TabsList>
            {(isFinance || isExecutive) && <TabsTrigger value="executives" data-testid={ACADEMY.commissionsExecutivesTab}>Ejecutivos</TabsTrigger>}
            {(isFinance || isInstructor) && <TabsTrigger value="instructors" data-testid={ACADEMY.commissionsInstructorsTab}>Instructores</TabsTrigger>}
          </TabsList>
        )}

        <TabsContent value="executives" className="mt-4">
          {!com ? <p className="text-muted-foreground">Cargando...</p> : myRow ? (
            <MyPanel row={myRow} month={month} tiers={com.tiers} />
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Tramos {com.mode === "flat" ? "(el porcentaje alcanzado va sobre todo lo cobrado)" : "(cada tramo sobre su parte)"}:{" "}
                {com.tiers.map((t, i) => (t.up_to == null ? `más de ${money(com.tiers[i - 1]?.up_to || 0)}: ${pct(t.percent)}` : `hasta ${money(t.up_to)}: ${pct(t.percent)}`)).join(" · ")}
                {com.include_reservations ? "" : " · la reserva no suma"}
              </p>
              <div className="bg-white border border-border rounded-xl overflow-x-auto" data-testid={ACADEMY.commissionsTable}>
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left px-3 py-2 font-normal">Ejecutivo</th><th className="text-right px-3 py-2 font-normal">Interesados</th>
                      <th className="text-right px-3 py-2 font-normal">Cerradas</th><th className="text-right px-3 py-2 font-normal">Vendido</th>
                      <th className="text-right px-3 py-2 font-normal">Cobrado</th><th className="text-right px-3 py-2 font-normal">Reembolsos</th>
                      <th className="text-right px-3 py-2 font-normal">%</th><th className="text-right px-3 py-2 font-normal">Comisión</th><th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {com.executives.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No hay ejecutivos cargados.</td></tr>}
                    {com.executives.map((r) => (
                      <Fragment key={r.executive_id}>
                        <tr className="border-b border-border" data-testid={`${ACADEMY.commissionRowPrefix}${r.executive_id}`}>
                          <td className="px-3 py-2">
                            <button type="button" onClick={() => toggle(r.executive_id)} className="inline-flex items-center gap-1 font-medium" data-testid={`${ACADEMY.commissionTogglePrefix}${r.executive_id}`}>
                              {open[r.executive_id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} {r.name}
                            </button>
                            <div><PayoutBadge payout={r.payout} current={r.commission} /></div>
                          </td>
                          <td className="text-right px-3 py-2">{r.leads}</td>
                          <td className="text-right px-3 py-2">{r.closed} / {r.enrollments}</td>
                          <td className="text-right px-3 py-2">{money(r.sold)}</td>
                          <td className="text-right px-3 py-2">{money(r.collected)}</td>
                          <td className="text-right px-3 py-2">{r.refunds ? `-${money(r.refunds)}` : ""}</td>
                          <td className="text-right px-3 py-2">{pct(r.percent)}</td>
                          <td className="text-right px-3 py-2 font-medium" data-testid={`${ACADEMY.commissionAmountPrefix}${r.executive_id}`}>{money(r.commission)}</td>
                          <td className="text-right px-3 py-2 whitespace-nowrap">
                            {isFinance && !r.payout && r.commission > 0 && (
                              <Button size="sm" variant="outline" onClick={() => setPayTarget({ kind: "executive", id: r.executive_id, name: r.name, amount: r.commission })} data-testid={`${ACADEMY.commissionPayPrefix}${r.executive_id}`}><Wallet className="w-4 h-4" /> Pagar</Button>
                            )}
                            {isFinance && r.payout && !r.payout.cash && (
                              <Button size="sm" variant="ghost" onClick={() => undo(r.payout)} title="Deshacer el pago"><Undo2 className="w-4 h-4" /></Button>
                            )}
                          </td>
                        </tr>
                        {open[r.executive_id] && <tr className="border-b border-border bg-[#F8FCFC]"><td colSpan={9} className="px-3 py-3"><ExecutiveDetail executiveId={r.executive_id} month={month} /></td></tr>}
                      </Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="text-sm font-medium">
                      <td className="px-3 py-2">Total</td><td /><td /><td className="text-right px-3 py-2">{money(com.totals.sold)}</td>
                      <td className="text-right px-3 py-2">{money(com.totals.collected)}</td><td className="text-right px-3 py-2">{com.totals.refunds ? `-${money(com.totals.refunds)}` : ""}</td>
                      <td /><td className="text-right px-3 py-2" data-testid={ACADEMY.commissionsTotal}>{money(com.totals.commission)}</td><td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="instructors" className="mt-4">
          {!ins ? <p className="text-muted-foreground">Cargando...</p> : (
            <div className="space-y-3" data-testid={ACADEMY.instructorsTable}>
              <p className="text-sm text-muted-foreground">
                Porcentaje de cada instructor por curso (se carga en Cursos y fechas) sobre {ins.base_mode === "price" ? "el precio de los alumnos activos" : "lo cobrado de los alumnos activos"} de cada fecha que empieza en el mes.
              </p>
              {ins.instructors.length === 0 && <div className="bg-white border border-border rounded-xl p-8 text-center text-muted-foreground">No hay fechas con instructor en este mes.</div>}
              {ins.instructors.map((r) => (
                <div key={r.instructor_id} className="bg-white border border-border rounded-xl p-4 space-y-3" data-testid={`${ACADEMY.instructorRowPrefix}${r.instructor_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium">{r.name}</p>
                      <PayoutBadge payout={r.payout} current={r.amount} />
                      {r.missing_rates > 0 && <p className="text-xs text-[#A34B49]">Falta cargar su porcentaje en {r.missing_rates === 1 ? "un curso" : `${r.missing_rates} cursos`}.</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-xl font-medium" data-testid={`${ACADEMY.instructorAmountPrefix}${r.instructor_id}`}>{money(r.amount)}</p>
                      {isFinance && !r.payout && r.amount > 0 && (
                        <Button size="sm" variant="outline" onClick={() => setPayTarget({ kind: "instructor", id: r.instructor_id, name: r.name, amount: r.amount })} data-testid={`${ACADEMY.instructorPayPrefix}${r.instructor_id}`}><Wallet className="w-4 h-4" /> Pagar</Button>
                      )}
                      {isFinance && r.payout && !r.payout.cash && <Button size="sm" variant="ghost" onClick={() => undo(r.payout)} title="Deshacer el pago"><Undo2 className="w-4 h-4" /></Button>}
                    </div>
                  </div>
                  <div className="divide-y divide-border border border-border rounded-lg">
                    {r.editions.map((e) => (
                      <div key={e.edition_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                        <span className="flex-1 min-w-[160px]">{e.course_name} · {fmtDate(e.start_date)}</span>
                        <span className="text-muted-foreground">{e.students} alumnos · base {money(e.base)}</span>
                        <span className={e.percent == null ? "text-[#A34B49]" : ""}>{e.percent == null ? "sin %" : pct(e.percent)}</span>
                        <span className="font-medium w-24 text-right">{money(e.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {isFinance && ins.instructors.length > 0 && <p className="text-right text-sm font-medium">Total instructores: {money(ins.total)}</p>}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <PayoutDialog target={payTarget} month={month} onClose={() => setPayTarget(null)} onDone={() => { toast({ title: "Pago registrado" }); load(); }} />
    </div>
  );
}
